// src/components/admin/page-management/section-forms/ProductCatalogVerticalForm.js
import React from "react";
import { Info } from "lucide-react";
import ColorPicker from "../../shared/ColorPicker";
import { useEffect, useState } from "react";
import axios from "axios";

const ProductCatalogVerticalForm = ({ content, updateContent }) => {
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [availableParentCategories, setAvailableParentCategories] = useState(
    []
  );
  const [loadingParentCategories, setLoadingParentCategories] = useState(false);
  const [loadedParentCategories, setLoadedParentCategories] = useState([]);

  // useEffect to fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (content?.showAllCategories === false) {
        try {
          setLoadingCategories(true);
          const response = await axios.get("/api/categories/basic");
          setAvailableCategories(response.data);

          // Initialize selected categories if not already set
          if (!content.selectedCategoryIds) {
            const initialSelected = response.data.map((cat) => cat._id);
            updateContent("selectedCategoryIds", initialSelected);
          }
        } catch (error) {
          console.error("Error fetching categories:", error);
        } finally {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();
  }, [content?.showAllCategories]);

  //useEffect to update the category selection UI
  useEffect(() => {
    const container = document.getElementById("category-selection-container");
    if (
      container &&
      availableCategories.length > 0 &&
      content?.showAllCategories === false
    ) {
      container.innerHTML = "";

      availableCategories.forEach((category) => {
        const isSelected =
          content.selectedCategoryIds?.includes(category._id) || false;

        const div = document.createElement("div");
        div.className = "flex items-center mb-2";

        const input = document.createElement("input");
        input.type = "checkbox";
        input.className = "mr-2";
        input.checked = isSelected;
        input.onchange = () => {
          const currentSelected = [...(content.selectedCategoryIds || [])];
          if (input.checked && !currentSelected.includes(category._id)) {
            updateContent("selectedCategoryIds", [
              ...currentSelected,
              category._id,
            ]);
          } else if (!input.checked && currentSelected.includes(category._id)) {
            updateContent(
              "selectedCategoryIds",
              currentSelected.filter((id) => id !== category._id)
            );
          }
        };

        const label = document.createElement("label");
        label.className = "text-sm";
        label.textContent = category.name;

        div.appendChild(input);
        div.appendChild(label);
        container.appendChild(div);
      });
    }
  }, [
    availableCategories,
    content?.selectedCategoryIds,
    content?.showAllCategories,
  ]);

  useEffect(() => {
    const fetchParentCategories = async () => {
      if (content?.showParentCategories === true) {
        try {
          setLoadingParentCategories(true);
          const response = await axios.get("/api/parent-categories/basic");
          setAvailableParentCategories(response.data);
          setLoadedParentCategories(response.data);

          // Initialize selected parent categories if not already set
          if (
            !content.selectedParentCategoryIds ||
            content.selectedParentCategoryIds.length === 0
          ) {
            const initialSelected = response.data.map((parent) => parent._id);
            updateContent("selectedParentCategoryIds", initialSelected);
          }
        } catch (error) {
          console.error("Error fetching parent categories:", error);
        } finally {
          setLoadingParentCategories(false);
        }
      }
    };

    fetchParentCategories();
  }, [content?.showParentCategories]);

  // Helper for handling color changes to ensure proper format
  const handleColorChange = (key, value) => {
    // Ensure color values include the # if missing
    if (value && !value.startsWith("#") && value.length > 0) {
      value = "#" + value;
    }
    updateContent(key, value);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Categories Section</h3>

      {/* Basic Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Basic Settings</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Section Title
          </label>
          <input
            type="text"
            value={content?.title || "Browse Categories"}
            onChange={(e) => updateContent("title", e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            className="w-full p-2 border rounded"
            rows="2"
            value={
              content?.description ||
              "Browse our selection of premium packaging solutions"
            }
            onChange={(e) => updateContent("description", e.target.value)}
            placeholder="Brief description of the category section"
          ></textarea>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Background Color
          </label>
          <ColorPicker
            color={content?.backgroundColor || "#F9FAFB"}
            onChange={(color) => updateContent("backgroundColor", color)}
          />
        </div>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.searchEnabled !== false}
              onChange={(e) => updateContent("searchEnabled", e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm">Enable Search</span>
          </label>
        </div>

        {content?.searchEnabled !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Search Placeholder
            </label>
            <input
              type="text"
              value={content?.searchPlaceholder || "Search categories..."}
              onChange={(e) =>
                updateContent("searchPlaceholder", e.target.value)
              }
              className="w-full p-2 border rounded"
            />
          </div>
        )}
      </div>

      {/* Layout Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Layout Settings</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Layout Type</label>
          <select
            value={content?.layoutType || "vertical"}
            onChange={(e) => updateContent("layoutType", e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="vertical">
              Vertical (Categories on left, Products on right)
            </option>
            <option value="slider">Horizontal Slider</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Slider Threshold
          </label>
          <div className="flex items-center">
            <input
              type="number"
              min="1"
              value={content?.sliderThreshold || 9}
              onChange={(e) =>
                updateContent("sliderThreshold", parseInt(e.target.value))
              }
              className="w-24 p-2 border rounded mr-2"
            />
            <span className="text-sm text-gray-600">
              Switch to slider if more categories than this number
            </span>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Category Image Shape
          </label>
          <select
            value={content?.categoryImageShape || "circle"}
            onChange={(e) =>
              updateContent("categoryImageShape", e.target.value)
            }
            className="w-full p-2 border rounded"
          >
            <option value="circle">Circle</option>
            <option value="square">Square with Rounded Corners</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Category Border Color
            </label>
            <ColorPicker
              color={content?.categoryBorderColor || "#3b82f6"}
              onChange={(color) => updateContent("categoryBorderColor", color)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Category Background Color
            </label>
            <ColorPicker
              color={content?.categoryBackgroundColor || "#f3f4f6"} // Default gray-100
              onChange={(color) =>
                updateContent("categoryBackgroundColor", color)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Category Text Color
            </label>
            <ColorPicker
              color={content?.categoryTextColor || "#1f2937"} // Default gray-800
              onChange={(color) => updateContent("categoryTextColor", color)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Selected Category Border Color
            </label>
            <ColorPicker
              color={content?.selectedCategoryBorderColor || "#3b82f6"}
              onChange={(color) =>
                updateContent("selectedCategoryBorderColor", color)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Selected Category Background
            </label>
            <ColorPicker
              color={content?.selectedCategoryBackgroundColor || "#60a5fa"} // Default blue-400
              onChange={(color) =>
                updateContent("selectedCategoryBackgroundColor", color)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Selected Category Text
            </label>
            <ColorPicker
              color={content?.selectedCategoryTextColor || "#ffffff"} // Default white
              onChange={(color) =>
                updateContent("selectedCategoryTextColor", color)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Category Hover Background
            </label>
            <ColorPicker
              color={content?.categoryHoverBackgroundColor || "#e5e7eb"} // Default gray-200
              onChange={(color) =>
                updateContent("categoryHoverBackgroundColor", color)
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Category Hover Text
            </label>
            <ColorPicker
              color={content?.categoryHoverTextColor || "#111827"} // Default gray-900
              onChange={(color) =>
                updateContent("categoryHoverTextColor", color)
              }
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Vertical Layout Image Size
          </label>
          <select
            value={content?.verticalImageSize || "medium"}
            onChange={(e) => updateContent("verticalImageSize", e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Slider Layout Image Size
          </label>
          <select
            value={content?.sliderImageSize || "medium"}
            onChange={(e) => updateContent("sliderImageSize", e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>

      {/* Category Selection */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h4 className="font-medium mb-4">Category Selection</h4>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showAllCategories !== false}
              onChange={(e) =>
                updateContent("showAllCategories", e.target.checked)
              }
              className="mr-2"
            />
            <span className="text-sm font-medium">Show All Categories</span>
          </label>
        </div>

        {content?.showAllCategories === false && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">
              Select Specific Categories
            </label>
            <div className="max-h-60 overflow-y-auto border rounded p-2">
              {/* This will be filled in the component */}
              <div className="text-sm text-gray-500 mb-2">
                Loading categories... If no categories appear, save the page
                first and edit again.
              </div>
              <div id="category-selection-container"></div>
            </div>
          </div>
        )}
      </div>

      {/* Parent Category Display Option */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h4 className="font-medium mb-4">Category Display Options</h4>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showParentCategories === true}
              onChange={(e) =>
                updateContent("showParentCategories", e.target.checked)
              }
              className="mr-2"
            />
            <span className="text-sm font-medium">Show Parent Categories</span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            When enabled, shows parent categories with related child categories
            in rows
          </p>
        </div>

        {content?.showParentCategories === true && (
          <div className="mb-4 mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={content?.showAllParentCategories !== false}
                onChange={(e) =>
                  updateContent("showAllParentCategories", e.target.checked)
                }
                className="mr-2"
              />
              <span className="text-sm font-medium">
                Show All Parent Categories
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              When disabled, you can select specific parent categories to
              display
            </p>
          </div>
        )}

        {content?.showParentCategories === true &&
          content?.showAllParentCategories === false && (
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">
                Select Specific Parent Categories
              </label>
              <div className="max-h-60 overflow-y-auto border rounded p-2">
                {loadingParentCategories ? (
                  <div className="text-sm text-gray-500 mb-2">
                    Loading parent categories...
                  </div>
                ) : loadedParentCategories.length > 0 ? (
                  loadedParentCategories.map((parentCategory) => (
                    <div
                      key={parentCategory._id}
                      className="flex items-center mb-2"
                    >
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={
                          content.selectedParentCategoryIds?.includes(
                            parentCategory._id
                          ) || false
                        }
                        onChange={(e) => {
                          const currentSelected = [
                            ...(content.selectedParentCategoryIds || []),
                          ];
                          if (
                            e.target.checked &&
                            !currentSelected.includes(parentCategory._id)
                          ) {
                            updateContent("selectedParentCategoryIds", [
                              ...currentSelected,
                              parentCategory._id,
                            ]);
                          } else if (
                            !e.target.checked &&
                            currentSelected.includes(parentCategory._id)
                          ) {
                            updateContent(
                              "selectedParentCategoryIds",
                              currentSelected.filter(
                                (id) => id !== parentCategory._id
                              )
                            );
                          }
                        }}
                      />
                      <label className="text-sm">{parentCategory.name}</label>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500">
                    No parent categories found. Try saving the page first and
                    edit again.
                  </div>
                )}
              </div>
            </div>
          )}

        {content?.showParentCategories === true && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Parent Category Display Style
            </label>
            <select
              value={content?.parentCategoryDisplayStyle || "nameAndImage"}
              onChange={(e) =>
                updateContent("parentCategoryDisplayStyle", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              <option value="nameAndImage">Name with Image</option>
              <option value="nameOnly">Name Only</option>
            </select>
          </div>
        )}

        {content?.showParentCategories === true && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Child Category Image Size
            </label>
            <select
              value={content?.childCategoryImageSize || "small"}
              onChange={(e) =>
                updateContent("childCategoryImageSize", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              <option value="tiny">Tiny</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
            </select>
          </div>
        )}
      </div>

      {/* Price Display Configuration */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Price Display Configuration</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Display Price Type
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.priceDisplayType || "regular"}
            onChange={(e) => updateContent("priceDisplayType", e.target.value)}
          >
            <option value="regular">Regular Price</option>
            <option value="tier">Pricing Tier (Last Bound)</option>
            <option value="wholesale">Wholesale Price</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Select which price to display for products when "Show Price" is
            enabled
          </p>
        </div>

        {/* Quantity Selection for Add to Cart */}
        <div className="mt-6">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="allowQuantitySelection"
              className="mr-2"
              checked={content?.allowQuantitySelection === true}
              onChange={(e) =>
                updateContent("allowQuantitySelection", e.target.checked)
              }
            />
            <label
              htmlFor="allowQuantitySelection"
              className="text-sm font-medium"
            >
              Allow Quantity Selection when Adding to Cart
            </label>
          </div>

          {content?.allowQuantitySelection && (
            <div className="mb-4 ml-6">
              <label className="block text-sm font-medium mb-1">
                Default Quantity
              </label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                min="1"
                value={
                  content?.defaultQuantity ||
                  (content?.priceDisplayType === "wholesale" ? 50 : 1)
                }
                onChange={(e) =>
                  updateContent(
                    "defaultQuantity",
                    parseInt(e.target.value, 10) ||
                      (content?.priceDisplayType === "wholesale" ? 50 : 1)
                  )
                }
              />
              <p className="text-xs text-gray-500 mt-1">
                {content?.priceDisplayType === "regular" &&
                  "For regular price, quantity can be incremented by 1 (default is 1)"}
                {content?.priceDisplayType === "tier" &&
                  "For tier pricing, quantity will follow tier bounds"}
                {content?.priceDisplayType === "wholesale" &&
                  "For wholesale price, quantity can be incremented by 1 (default is 50)"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* User Visibility Configuration */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Price and Cart Visibility</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Show Price For
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.priceVisibility || "all"}
            onChange={(e) => updateContent("priceVisibility", e.target.value)}
          >
            <option value="all">Everyone (Default)</option>
            <option value="authenticated">Logged In Users Only</option>
            <option value="business">Business Users Only</option>
            <option value="regular">Regular Users Only</option>
            <option value="none">No One (Hide Prices)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Control which types of users can see product pricing
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Add to Cart For
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.cartVisibility || "all"}
            onChange={(e) => updateContent("cartVisibility", e.target.value)}
          >
            <option value="all">Everyone (Default)</option>
            <option value="authenticated">Logged In Users Only</option>
            <option value="business">Business Users Only</option>
            <option value="regular">Regular Users Only</option>
            <option value="none">No One (Disable Add to Cart)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Control which types of users can add products to cart
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Login Prompt Message
          </label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Please login to see prices and purchase products"
            value={
              content?.loginPromptMessage ||
              "Please login to see prices and purchase products"
            }
            onChange={(e) =>
              updateContent("loginPromptMessage", e.target.value)
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            Message to show when user doesn't have permission to see prices or
            add to cart
          </p>
        </div>
      </div>

      {/* Product Display Configuration */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Product Information Display</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Information Fields
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="showName"
                className="mr-2"
                checked={content?.productDisplayFields?.showName !== false}
                onChange={(e) =>
                  updateContent("productDisplayFields", {
                    ...(content?.productDisplayFields || {}),
                    showName: e.target.checked,
                  })
                }
              />
              <label htmlFor="showName" className="text-sm">
                Show Product Name
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showPrice"
                className="mr-2"
                checked={content?.productDisplayFields?.showPrice !== false}
                onChange={(e) =>
                  updateContent("productDisplayFields", {
                    ...(content?.productDisplayFields || {}),
                    showPrice: e.target.checked,
                  })
                }
              />
              <label htmlFor="showPrice" className="text-sm">
                Show Price
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showDescription"
                className="mr-2"
                checked={
                  content?.productDisplayFields?.showDescription === true
                }
                onChange={(e) =>
                  updateContent("productDisplayFields", {
                    ...(content?.productDisplayFields || {}),
                    showDescription: e.target.checked,
                  })
                }
              />
              <label htmlFor="showDescription" className="text-sm">
                Show Description
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showNote"
                className="mr-2"
                checked={content?.productDisplayFields?.showNote === true}
                onChange={(e) =>
                  updateContent("productDisplayFields", {
                    ...(content?.productDisplayFields || {}),
                    showNote: e.target.checked,
                  })
                }
              />
              <label htmlFor="showNote" className="text-sm">
                Show Note
              </label>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Product Info Position
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productInfoPosition || "below"}
            onChange={(e) =>
              updateContent("productInfoPosition", e.target.value)
            }
          >
            <option value="below">Below Image</option>
            <option value="right">Right of Image</option>
            <option value="left">Left of Image</option>
            <option value="overlay">Overlay on Image</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Text Alignment
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productTextAlignment || "left"}
            onChange={(e) =>
              updateContent("productTextAlignment", e.target.value)
            }
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Product Name Max Lines
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productNameLines || 2}
            onChange={(e) =>
              updateContent("productNameLines", parseInt(e.target.value, 10))
            }
          >
            <option value={1}>1 Line</option>
            <option value={2}>2 Lines</option>
            <option value={3}>3 Lines</option>
            <option value={0}>No Limit</option>
          </select>
        </div>

        {content?.productDisplayFields?.showDescription && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Description Max Lines
            </label>
            <select
              className="w-full p-2 border rounded"
              value={content?.productDescriptionLines || 3}
              onChange={(e) =>
                updateContent(
                  "productDescriptionLines",
                  parseInt(e.target.value, 10)
                )
              }
            >
              <option value={1}>1 Line</option>
              <option value={2}>2 Lines</option>
              <option value={3}>3 Lines</option>
              <option value={4}>4 Lines</option>
              <option value={5}>5 Lines</option>
              <option value={0}>No Limit</option>
            </select>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Image Aspect Ratio
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productImageAspectRatio || "square"}
            onChange={(e) =>
              updateContent("productImageAspectRatio", e.target.value)
            }
          >
            <option value="square">Square (1:1)</option>
            <option value="portrait">Portrait (3:4)</option>
            <option value="landscape">Landscape (4:3)</option>
            <option value="original">Original (As uploaded)</option>
          </select>
        </div>
      </div>

      {/* Product Interaction Configuration */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Product Click Behavior</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            When a product is clicked:
          </label>
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="productClickNavigate"
                name="productClickBehavior"
                className="mr-2"
                checked={content?.productClickBehavior !== "popup"}
                onChange={() =>
                  updateContent("productClickBehavior", "navigate")
                }
              />
              <label htmlFor="productClickNavigate" className="text-sm">
                Navigate to product detail page
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="radio"
                id="productClickPopup"
                name="productClickBehavior"
                className="mr-2"
                checked={content?.productClickBehavior === "popup"}
                onChange={() => updateContent("productClickBehavior", "popup")}
              />
              <label htmlFor="productClickPopup" className="text-sm">
                Show product details in popup
              </label>
            </div>
          </div>
        </div>

        {content?.productClickBehavior === "popup" && (
          <div className="border-t pt-4 mt-2">
            <h5 className="font-medium text-sm mb-3">Popup Settings</h5>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Information to Display
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showGallery"
                    className="mr-2"
                    checked={
                      content?.productPopupSettings?.showGallery !== false
                    }
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showGallery: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showGallery" className="text-sm">
                    Image Gallery
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPopupDescription"
                    className="mr-2"
                    checked={
                      content?.productPopupSettings?.showDescription !== false
                    }
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showDescription: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showPopupDescription" className="text-sm">
                    Description
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPopupPrice"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showPrice !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showPrice: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showPopupPrice" className="text-sm">
                    Price Information
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showPopupNote"
                    className="mr-2"
                    checked={content?.productPopupSettings?.showNote !== false}
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showNote: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showPopupNote" className="text-sm">
                    Product Note
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showAttributes"
                    className="mr-2"
                    checked={
                      content?.productPopupSettings?.showAttributes !== false
                    }
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showAttributes: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showAttributes" className="text-sm">
                    Product Attributes
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Action Buttons
              </label>
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showAddToCart"
                    className="mr-2"
                    checked={
                      content?.productPopupSettings?.showAddToCart !== false
                    }
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showAddToCart: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showAddToCart" className="text-sm">
                    Add to Cart Button
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCustomizeButton"
                    className="mr-2"
                    checked={
                      content?.productPopupSettings?.showCustomizeButton !==
                      false
                    }
                    onChange={(e) =>
                      updateContent("productPopupSettings", {
                        ...(content?.productPopupSettings || {}),
                        showCustomizeButton: e.target.checked,
                      })
                    }
                  />
                  <label htmlFor="showCustomizeButton" className="text-sm">
                    Customize Product Button
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Popup Size
              </label>
              <select
                className="w-full p-2 border rounded"
                value={content?.productPopupSettings?.popupSize || "medium"}
                onChange={(e) =>
                  updateContent("productPopupSettings", {
                    ...(content?.productPopupSettings || {}),
                    popupSize: e.target.value,
                  })
                }
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="fullscreen">Full Screen</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Product Grid Configuration */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Product Grid Configuration</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Mobile Columns
            </label>
            <select
              value={content?.productGridColumnsResponsive?.mobile || 1}
              onChange={(e) =>
                updateContent("productGridColumnsResponsive", {
                  ...(content?.productGridColumnsResponsive || {}),
                  mobile: parseInt(e.target.value),
                })
              }
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Tablet Columns
            </label>
            <select
              value={content?.productGridColumnsResponsive?.tablet || 2}
              onChange={(e) =>
                updateContent("productGridColumnsResponsive", {
                  ...(content?.productGridColumnsResponsive || {}),
                  tablet: parseInt(e.target.value),
                })
              }
              className="w-full p-2 border rounded"
            >
              <option value={1}>1 Column</option>
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Desktop Columns
            </label>
            <select
              value={content?.productGridColumnsResponsive?.desktop || 3}
              onChange={(e) =>
                updateContent("productGridColumnsResponsive", {
                  ...(content?.productGridColumnsResponsive || {}),
                  desktop: parseInt(e.target.value),
                })
              }
              className="w-full p-2 border rounded"
            >
              <option value={2}>2 Columns</option>
              <option value={3}>3 Columns</option>
              <option value={4}>4 Columns</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Product Card Style
          </label>
          <select
            className="w-full p-2 border rounded"
            value={content?.productCardStyle || "shadow"}
            onChange={(e) => updateContent("productCardStyle", e.target.value)}
          >
            <option value="shadow">Shadow</option>
            <option value="outline">Outline</option>
            <option value="flat">Flat</option>
          </select>
        </div>

        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="showQuickAction"
            className="mr-2"
            checked={content?.showQuickAction !== false}
            onChange={(e) => updateContent("showQuickAction", e.target.checked)}
          />
          <label htmlFor="showQuickAction" className="text-sm font-medium">
            Show Quick Action Button
          </label>
        </div>

        {content?.showQuickAction !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Quick Action Icon
            </label>
            <select
              className="w-full p-2 border rounded"
              value={content?.quickActionIcon || "customize"}
              onChange={(e) => updateContent("quickActionIcon", e.target.value)}
            >
              <option value="customize">Customize (Pencil)</option>
              <option value="cart">Add to Cart</option>
            </select>
          </div>
        )}

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.fadeEffectEnabled !== false}
              onChange={(e) =>
                updateContent("fadeEffectEnabled", e.target.checked)
              }
              className="mr-2"
            />
            <span className="text-sm">Enable Fade-in Animation</span>
          </label>
        </div>
      </div>

      {/* Load More Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h4 className="font-medium mb-4">Load More Settings</h4>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Load More Button Text
          </label>
          <input
            type="text"
            value={content?.loadMoreButtonText || "Load More"}
            onChange={(e) =>
              updateContent("loadMoreButtonText", e.target.value)
            }
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Load More Button Color
          </label>
          <ColorPicker
            color={content?.loadMoreButtonColor || "#1e3a8a"}
            onChange={(color) => updateContent("loadMoreButtonColor", color)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Desktop Load Increment
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={content?.loadIncrementDesktop || 6}
              onChange={(e) =>
                updateContent("loadIncrementDesktop", parseInt(e.target.value))
              }
              className="w-full p-2 border rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of products to load each time on desktop
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Mobile Load Increment
            </label>
            <input
              type="number"
              min="1"
              max="12"
              value={content?.loadIncrementMobile || 3}
              onChange={(e) =>
                updateContent("loadIncrementMobile", parseInt(e.target.value))
              }
              className="w-full p-2 border rounded"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of products to load each time on mobile
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={content?.showLoadingAnimation !== false}
              onChange={(e) =>
                updateContent("showLoadingAnimation", e.target.checked)
              }
              className="mr-2"
            />
            <span className="text-sm">Show Loading Animation</span>
          </label>
        </div>

        {content?.showLoadingAnimation !== false && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Loading Animation URL
            </label>
            <input
              type="text"
              value={content?.loadingAnimationUrl || "../images/loading.gif"}
              onChange={(e) =>
                updateContent("loadingAnimationUrl", e.target.value)
              }
              className="w-full p-2 border rounded"
            />
          </div>
        )}
      </div>

      {/* API Settings */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">API Settings</h4>
          <div className="text-blue-500">
            <Info size={16} />
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          These settings are for advanced users. Most users should leave these
          at their default values.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Categories API Path
          </label>
          <input
            type="text"
            value={content?.apiPath || "/api/categories/basic"}
            onChange={(e) => updateContent("apiPath", e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Products API Path
          </label>
          <input
            type="text"
            value={content?.productApiPath || "/api/products/basic"}
            onChange={(e) => updateContent("productApiPath", e.target.value)}
            className="w-full p-2 border rounded"
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogVerticalForm;
